import { useState } from 'react';
import { Bell } from 'lucide-react';

import { useTranslation } from '@/utils/i18n';
import { PrimaryButton } from '@/components/Buttons';

interface Props {
  open: boolean;
  /** User tapped "Enable" — caller fires the OS prompt via registerForPush(). */
  onAccept: () => Promise<void> | void;
  /** User tapped "Not now" — caller records the dismissal, no OS prompt. */
  onDecline: () => void;
}

/**
 * First-login soft-ask (pre-permission) modal. Shown BEFORE the OS
 * notification prompt so the one-shot iOS prompt is only fired for users who
 * already said yes — a cold "Don't Allow" is Settings-only to undo.
 */
export default function NotificationPermissionModal({ open, onAccept, onDecline }: Props) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  return (
    <div
      className="tw:fixed tw:inset-0 tw:z-50 tw:flex tw:items-center tw:justify-center tw:bg-black/60 tw:px-6"
      role="dialog"
      aria-modal="true"
      data-testid="modal-push-permission"
    >
      <div className="tw:w-full tw:max-w-[340px] tw:rounded-2xl tw:bg-dark-1 tw:p-6 tw:text-center">
        <span className="tw:mx-auto tw:flex tw:size-14 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-primary">
          <Bell size={26} className="tw:text-white" />
        </span>
        <h3 className="tw:mt-4 tw:heading-3 tw:text-white">{t('pushPrompt.title')}</h3>
        <p className="tw:mt-2 tw:text-base tw:text-grey-3">{t('pushPrompt.body')}</p>

        <div className="tw:mt-5">
          <PrimaryButton
            label={t('pushPrompt.accept')}
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onAccept();
              } finally {
                setBusy(false);
              }
            }}
            data-testid="btn-push-accept"
          />
        </div>
        <button
          type="button"
          onClick={onDecline}
          disabled={busy}
          data-testid="btn-push-later"
          className="tw:mt-3 tw:w-full tw:py-2 tw:text-base tw:text-grey-3"
        >
          {t('pushPrompt.later')}
        </button>
      </div>
    </div>
  );
}
